import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  Typography,
  Sheet,
  Box,
} from "@mui/joy";
import { Select, MenuItem, Chip, InputLabel } from "@mui/material";
import toast from "react-hot-toast";
import AuthContext from "../../contexts/authContext";
import axiosInstance from "../../api/axios";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function AddUser({ openUser, setOpenUser, editId, setEditId }) {
  const { userData, setRefresh } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  // no TS types here—just an empty array
  const [area, setArea] = useState([]);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    username: "",
    password: "",
    role: "agent",
  });

  useEffect(() => {
    if (editId) {
      setEditMode(true);
      const editUser = userData?.users?.find((u) => u._id === editId);
      if (editUser) {
        setForm({
          name: editUser.name || "",
          mobile: editUser.mobile || "",
          username: editUser.username || "",
          password: "",
          role: editUser.role || "agent",
        });
        setArea(editUser.areas?.map((a) => a._id) || []);
      }
    } else {
      setEditMode(false);
      setForm({
        name: "",
        mobile: "",
        username: "",
        password: "",
        role: "agent",
      });
      setArea([]);
    }
  }, [editId, userData]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, mobile, username, password, role } = form;
    if (mobile.length !== 10) {
      toast.error("Mobile number must be 10 digits.");
      return;
    }
    if (
      !name ||
      !mobile ||
      !username ||
      !password ||
      !role ||
      area.length === 0
    ) {
      toast.error("All fields are required.");
      return;
    }

    const payload = { name, mobile, username, password, role, area };

    try {
      const res = editMode
        ? await axiosInstance.put(`/users/edit/${editId}`, payload)
        : await axiosInstance.post("/users/add", payload);

      if (res.status === 200) {
        toast.success(editMode ? "User updated!" : "User added!");
        setRefresh(true);
        setOpenUser(false);
        setEditId("");
        setForm({
          name: "",
          mobile: "",
          username: "",
          password: "",
          role: "agent",
        });
        setArea([]);
      }
    } catch {
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleClose = () => {
    setOpenUser(false);
    setEditId("");
    setForm({
      name: "",
      mobile: "",
      username: "",
      password: "",
      role: "agent",
    });
    setArea([]);
  };

  return (
    <Modal
      open={openUser}
      onClose={handleClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: 340,
          width: "100%",
          bgcolor: "#FEDDDD",
          p: 3,
          borderRadius: "md",
          boxShadow: "lg",
        }}
      >
        <ModalClose
          onClick={handleClose}
          sx={{
            m: 1,
            "& svg": { color: "#800001" },
            "&:hover": {
              backgroundColor: "#800001",
              "& svg": { color: "#fff" },
            },
          }}
        />
        <Typography
          level="h4"
          sx={{
            color: "#800001",
            fontWeight: "bold",
            mb: 2,
            textAlign: "center",
          }}
        >
          {editMode ? "Edit User" : "Add User"}
        </Typography>

        <form onSubmit={handleSubmit}>
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Name</FormLabel>
            <Input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Mobile</FormLabel>
            <Input
              name="mobile"
              type="tel"
              value={form.mobile}
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Username</FormLabel>
            <Input
              name="username"
              value={form.username}
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Password</FormLabel>
            <Input
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Role</FormLabel>

            <Select
              fullWidth
              value={form.role}
              onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
              required
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="employee">Employee</MenuItem>
              <MenuItem value="agent">Agent</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Area</FormLabel>
            <InputLabel id="area-label" sx={{ display: "none" }} />
            <Select
              labelId="area-label"
              multiple
              value={area}
              onChange={(e) => setArea(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((id) => {
                    const label =
                      userData.areas.find((a) => a._id === id)?.areaName || id;
                    return (
                      <Chip
                        key={id}
                        label={label}
                        sx={{
                          backgroundColor: "#800001",
                          color: "#fff",
                          height: 24,
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
              sx={{
                width: "100%",
                backgroundColor: "#FEDDDD",
                borderRadius: 1,
              }}
            >
              {userData?.areas
                ?.filter((a) => a.status !== "erased")
                .map((a) => {
                  const isSelected = area.includes(String(a._id));

                  return (
                    <MenuItem
                      key={a._id}
                      value={a._id}
                      sx={{
                        border: "1px solid #fff",
                        backgroundColor: isSelected
                          ? "#800001 !important"
                          : "#fff",
                        color: isSelected ? "#fff !important" : "#000",

                        "&.Mui-selected": {
                          backgroundColor: "#800001 !important",
                          color: "#fff !important",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#800001 !important",
                          color: "#fff !important",
                        },

                        "&:hover": {
                          backgroundColor: isSelected ? "#800001" : "#f0f0f0",
                          color: isSelected ? "#fff" : "#000",
                        },
                      }}
                    >
                      {a.areaName}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="solid"
            sx={{
              backgroundColor: "#800001",
              color: "#fff",
              "&:hover": { backgroundColor: "#800001", opacity: 0.9 },
            }}
          >
            {editMode ? "Update User" : "Submit User"}
          </Button>
        </form>
      </Sheet>
    </Modal>
  );
}
